import {
  Notification,
  ParsedNotification,
  NotificationType,
  NotificationContent,
  CVNotUpdatedNotificationContent,
  LowCVScoreNotificationContent,
  ExpiringCertificationContent,
  OnBenchUpdateCVNotificationContent,
  PeopleAvailableSoonNotificationContent
} from '../../../types/notification';

/**
 * Transform backend notification content to match frontend expectations
 * Backend uses PascalCase, frontend expects camelCase
 */
function transformNotificationContent(content: any, type: NotificationType): any {
  if (!content || typeof content !== 'object') {
    return content;
  }

  const transformed = { ...content };

  // Transform common properties
  if ('PersonId' in content) transformed.personId = content.PersonId;
  if ('PersonEmail' in content) transformed.personEmail = content.PersonEmail;
  if ('PersonFirstName' in content) transformed.personFirstName = content.PersonFirstName;
  if ('PersonLastName' in content) transformed.personLastName = content.PersonLastName;

  // Transform type-specific properties
  switch (type) {
    case NotificationType.CVNotUpdated:
      if ('LastUpdateDate' in content) transformed.lastUpdateDate = content.LastUpdateDate;
      if ('DaysSinceUpdate' in content) transformed.daysSinceUpdate = content.DaysSinceUpdate;
      if ('ThresholdDays' in content) transformed.thresholdDays = content.ThresholdDays;
      break;

    case NotificationType.LowCVScore:
      if ('CurrentScore' in content) transformed.currentScore = content.CurrentScore;
      if ('ThresholdScore' in content) transformed.thresholdScore = content.ThresholdScore;
      if ('ScoreDeficit' in content) transformed.scoreDeficit = content.ScoreDeficit;
      break;

    case NotificationType.CertificationExpiringSoon:
      if ('CertificateTitle' in content) transformed.certificateTitle = content.CertificateTitle;
      if ('DaysUntilExpiry' in content) transformed.daysUntilExpiry = content.DaysUntilExpiry;
      if ('ExpirationDate' in content) transformed.expirationDate = content.ExpirationDate;
      break;

    case NotificationType.OnBenchUpdateCV:
      if ('LastCVUpdate' in content) transformed.lastCVUpdate = content.LastCVUpdate;
      if ('AvailabilityDate' in content) transformed.availabilityDate = content.AvailabilityDate;
      break;

    case NotificationType.PeopleAvailableSoon:
      if ('People' in content && Array.isArray(content.People)) {
        transformed.people = content.People.map((person: any) => ({
          personId: person.PersonId,
          personEmail: person.PersonEmail,
          personFirstName: person.PersonFirstName,
          personLastName: person.PersonLastName,
          availabilityDate: person.AvailabilityDate,
          daysUntilAvailable: person.DaysUntilAvailable
        }));
      }
      if ('ThresholdDays' in content) transformed.thresholdDays = content.ThresholdDays;
      if ('PeopleCount' in content) transformed.peopleCount = content.PeopleCount;
      break;
  }

  return transformed;
}

/**
 * Parse a raw notification into a typed notification with structured content
 */
export function parseNotification(notification: Notification): ParsedNotification {
  try {
    const type = notification.type as NotificationType;
    const rawContent = JSON.parse(notification.content);
    
    // Transform the content to match frontend expectations
    const content = transformNotificationContent(rawContent, type) as NotificationContent;
    
    return {
      ...notification,
      type,
      content,
      rawContent: notification.content
    };
  } catch (error) {
    console.error('Failed to parse notification content:', error, notification);
    throw new Error(`Invalid notification content for notification ${notification.id}`);
  }
}

/**
 * Parse multiple notifications
 */
export function parseNotifications(notifications: Notification[]): ParsedNotification[] {
  return notifications.map(parseNotification);
}

/**
 * Type guards for notification content
 */
export function isCVNotUpdatedContent(
  content: NotificationContent
): content is CVNotUpdatedNotificationContent {
  return 'lastUpdateDate' in content && 'daysSinceUpdate' in content && 'thresholdDays' in content;
}

export function isLowCVScoreContent(
  content: NotificationContent
): content is LowCVScoreNotificationContent {
  return 'currentScore' in content && 'thresholdScore' in content && 'scoreDeficit' in content;
}

export function isExpiringCertificationContent(
  content: NotificationContent
): content is ExpiringCertificationContent {
  return 'certificateTitle' in content && 'daysUntilExpiry' in content && 'expirationDate' in content;
}

export function isOnBenchUpdateCVContent(
  content: NotificationContent
): content is OnBenchUpdateCVNotificationContent {
  return 'lastCVUpdate' in content || 'availabilityDate' in content;
}

export function isPeopleAvailableSoonContent(
  content: NotificationContent
): content is PeopleAvailableSoonNotificationContent {
  return 'people' in content && 'thresholdDays' in content && 'peopleCount' in content;
}

/**
 * Get notification type from parsed notification
 */
export function getNotificationType(notification: ParsedNotification): NotificationType {
  return notification.type;
}

/**
 * Check if notification is of specific type
 */
export function isNotificationType<T extends NotificationType>(
  notification: ParsedNotification,
  type: T
): boolean {
  return notification.type === type;
}

/**
 * Filter notifications by type
 */
export function filterNotificationsByType<T extends NotificationType>(
  notifications: ParsedNotification[],
  type: T
): ParsedNotification[] {
  return notifications.filter(notification => notification.type === type);
}

/**
 * Get content for specific notification type with type safety
 */
export function getTypedContent<T extends NotificationType>(
  notification: ParsedNotification,
  expectedType: T
): NotificationContent | null {
  if (notification.type !== expectedType) {
    return null;
  }
  return notification.content;
}

/**
 * Validate notification content structure
 */
export function validateNotificationContent(
  type: NotificationType,
  content: any
): boolean {
  try {
    switch (type) {
      case NotificationType.CVNotUpdated:
        return isCVNotUpdatedContent(content);
      case NotificationType.LowCVScore:
        return isLowCVScoreContent(content);
      case NotificationType.CertificationExpiringSoon:
        return isExpiringCertificationContent(content);
      case NotificationType.OnBenchUpdateCV:
        return isOnBenchUpdateCVContent(content);
      case NotificationType.PeopleAvailableSoon:
        return isPeopleAvailableSoonContent(content);
      default:
        return false;
    }
  } catch {
    return false;
  }
}

/**
 * Safe parse notification with validation
 */
export function safeParseNotification(notification: Notification): ParsedNotification | null {
  try {
    const parsed = parseNotification(notification);
    const isValid = validateNotificationContent(parsed.type, parsed.content);
    
    if (!isValid) {
      console.warn('Invalid notification content structure:', notification);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Failed to safely parse notification:', error, notification);
    return null;
  }
}

/**
 * Safe parse multiple notifications, filtering out invalid ones
 */
export function safeParseNotifications(notifications: Notification[]): ParsedNotification[] {
  return notifications
    .map(safeParseNotification)
    .filter((notification): notification is ParsedNotification => notification !== null);
} 